import { head, pipe, split } from 'ramda';
import { useIntl } from 'react-intl';
import { generatePath, useLocation, useMatch, useParams } from 'react-router';

import { BreadcrumbElementType, PATHS } from '@/shared/config';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { getName, isPreviewPage } from '@/shared/lib';

import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { getDisplayToothNumber, toothModel } from '@/entities/tooth';
import {
  transcriptorMessages,
  transcriptorModel,
} from '@/entities/transcriptor';
import { userModel } from '@/entities/user';

import { useGetReportConfigs } from './useGetReportConfigs';

export const useBreadcrumbs = (
  currentPageLabel = '',
): Record<string, BreadcrumbElementType> => {
  const params = useParams();
  const { search } = useLocation();
  const { formatMessage } = useIntl();
  const patientPageMatch = useMatch(PATHS.patient);
  const consultationPageMatch = useMatch(PATHS.consultation);
  const dentalPhotosPageMatch = useMatch(PATHS.dentalPhotos);
  const recordingsPageMatch = useMatch(PATHS.patientRecordings);
  const { pathname } = useLocation();

  const { isWide, isLarge, isMobile } = useMedia();

  const patientID = params.patientID ?? '';
  const reportID = params.reportID ?? '';
  const toothID = params.toothID ?? '';
  const isPrintPage = isPreviewPage(pathname);
  const isPatientProfile = patientPageMatch !== null;
  const isConsultationPage = consultationPageMatch !== null;
  const isDentalPhotosPage = dentalPhotosPageMatch !== null;
  const isRecordingsPage = recordingsPageMatch !== null;

  // Get consultation ID from URL query params
  const searchParams = new URLSearchParams(search);
  const consultationId = searchParams.get('id');

  const currentPatient = useAppSelector(
    patientModel.selectors.selectPatientByID(patientID),
  );

  const currentTooth = useAppSelector(
    toothModel.selectors.selectToothByID(toothID),
  );
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const dentalNotationFormat = useAppSelector(
    organizationModel.selectors.selectDentalNotationFormat,
  );

  // Get current consultation if on consultation page
  const currentConsultation = useAppSelector((state) =>
    consultationId && isConsultationPage
      ? transcriptorModel.selectors.selectConsultationById(
          state,
          consultationId,
        )
      : null,
  );

  const { reportLabel, reportPath } = useGetReportConfigs({
    patientID,
    reportID,
    isWide,
    isMobile,
  });

  const patientLabel = getName(
    currentPatient?.PersonalData?.FirstName,
    currentPatient?.PersonalData?.LastName,
    {
      userLocale,
      shortFirstName: !isPatientProfile && (!isLarge || !isWide),
      shortLastName: !isPatientProfile && !isLarge,
    },
  );

  const patientProfilePath = generatePath(PATHS.patient, {
    patientID,
  });

  const toothNumber = getDisplayToothNumber(
    currentTooth?.Numeration?.ISO ?? 0,
    dentalNotationFormat,
  );

  const toothLabel = toothNumber
    ? `${formatMessage({
        id: 'global.tooth',
        defaultMessage: 'Tooth',
      })} ${toothNumber}`
    : '';

  const printLabel = isPrintPage
    ? formatMessage({
        id: 'global.printReport',
        defaultMessage: 'Print Report',
      })
    : '';

  const shortPrintLabel = printLabel
    ? `${pipe(split(' '), head)(printLabel)}...`
    : '';

  const consultationLabel = isConsultationPage
    ? currentConsultation?.name ||
      currentConsultation?.schemaName ||
      formatMessage(transcriptorMessages.consultationTypeDental)
    : '';

  const dentalPhotosLabel = isDentalPhotosPage
    ? formatMessage({
        id: 'dentalPhotosPage.title',
        defaultMessage: 'Dental photos',
      })
    : '';

  const recordingsLabel =
    isRecordingsPage || isConsultationPage
      ? formatMessage({
          id: 'patientRecordings.title',
          defaultMessage: 'Recordings',
        })
      : '';

  const recordingsPath = isConsultationPage
    ? generatePath(PATHS.patientRecordings, { patientID })
    : '';

  return {
    patient: {
      label: patientLabel,
      path:
        reportID ||
        params.documentID ||
        isConsultationPage ||
        isDentalPhotosPage ||
        isRecordingsPage
          ? patientProfilePath
          : '',
    },
    document: { label: params.documentID ? currentPageLabel : '', path: '' },
    report: {
      label: reportLabel,
      path: toothID || isPrintPage ? reportPath || '' : '',
    },
    recordings: {
      label: recordingsLabel,
      path: recordingsPath,
    },
    consultation: {
      label: consultationLabel,
      path: '',
    },
    dentalPhotos: {
      label: dentalPhotosLabel,
      path: '',
    },
    tooth: {
      label: toothLabel,
      path: '',
    },
    print: {
      label: isWide ? printLabel : shortPrintLabel,
      path: '',
    },
  };
};
